// Building blocks for the ClearMakes Inbox search pages (integrations, alternatives,
// guides, segments, templates). Written 2026-09-29 for the SEO run in
// gtm/products/clearmakes-inbox/seo/.
//
// These pages sit inside the `.legacy` style scope (App.tsx), which remaps classes such
// as text-white, bg-[#0A0A0A], bg-white/10, border-white/*, gradients, blurs and
// shadow-[...]. So this file uses hsl() arbitrary colours only, never those tokens.
// Colours: ClearMakes blue, ink, navy, pale blue, greys. Icons black or white only.
import type { ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import { SmartLink } from "@/components/Header";

/* ── Facts every page repeats (from inbox/subscription_plans.py and /pricing) ── */
export const INBOX = {
  name: "ClearMakes Inbox",
  url: "https://clearmakes.com/products/inbox",
  startUrl: "/login?view=signup",
  cta: "Try Inbox free",
  /** Tier 1 (US dollar) prices; /pricing shows local prices where they differ. */
  prices: { free: 0, starter: 29, growth: 59, enterprise: 39 },
  updated: "29 September 2026",
  updatedIso: "2026-09-29",
  team: "The ClearMakes Inbox team",
};

/** SoftwareApplication for ClearMakes Inbox, one Offer per plan (prices match /pricing). */
export const inboxAppLd = (description: string) => ({
  "@type": "SoftwareApplication",
  "@id": "https://clearmakes.com/products/inbox#app",
  name: "ClearMakes Inbox",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  url: INBOX.url,
  description,
  offers: [
    { "@type": "Offer", name: "Free", price: "0", priceCurrency: "USD" },
    { "@type": "Offer", name: "Starter", price: "29", priceCurrency: "USD", description: "Per month" },
    { "@type": "Offer", name: "Growth", price: "59", priceCurrency: "USD", description: "Per month" },
    { "@type": "Offer", name: "Enterprise", price: "39", priceCurrency: "USD", description: "Per seat, per month" },
  ],
  publisher: { "@type": "Organization", name: "ClearMakes", url: "https://clearmakes.com" },
});

/** An Article for guides and templates. */
export const articleLd = (headline: string, description: string, path: string) => ({
  "@type": "Article",
  headline,
  description,
  url: `https://clearmakes.com${path}`,
  dateModified: INBOX.updatedIso,
  author: { "@type": "Organization", name: "ClearMakes", url: "https://clearmakes.com" },
  publisher: { "@type": "Organization", name: "ClearMakes", url: "https://clearmakes.com" },
});

export const JsonLd = ({ data }: { data: object | object[] }) => (
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{
      __html: JSON.stringify(Array.isArray(data) ? { "@context": "https://schema.org", "@graph": data } : { "@context": "https://schema.org", ...data }),
    }}
  />
);

/* ── Layout ─────────────────────────────────────────────────────────────── */
export const wrap = "mx-auto w-full max-w-[1120px] px-4 sm:px-8";

const tones = {
  pale: "bg-[hsl(216_60%_97%)] text-[hsl(0_0%_4%)]",
  pale2: "bg-[hsl(216_85%_94%)] text-[hsl(0_0%_4%)]",
  white: "bg-[hsl(0_0%_100%)] text-[hsl(0_0%_4%)]",
  navy: "bg-[hsl(222_60%_10%)] text-[hsl(0_0%_100%)]",
  blue: "bg-[linear-gradient(160deg,hsl(216_84%_52%)_0%,hsl(218_80%_44%)_45%,hsl(218_81%_23%)_100%)] text-[hsl(0_0%_100%)]",
};
export type Tone = keyof typeof tones;
const isDark = (t: Tone) => t === "navy" || t === "blue";

const btnBase =
  "inline-flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-6 text-[16px] font-medium transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[hsl(216_84%_52%)]";
export const btn = {
  ink: `${btnBase} bg-[hsl(0_0%_4%)] text-[hsl(0_0%_100%)] hover:bg-[hsl(0_0%_15%)]`,
  line: `${btnBase} border border-[hsl(216_40%_80%)] bg-[hsl(0_0%_100%)] text-[hsl(0_0%_4%)] hover:bg-[hsl(216_60%_97%)]`,
  white: `${btnBase} bg-[hsl(0_0%_100%)] text-[hsl(0_0%_4%)] hover:bg-[hsl(0_0%_90%)]`,
  ghost: `${btnBase} border border-[hsl(0_0%_100%/0.35)] text-[hsl(0_0%_100%)] hover:bg-[hsl(0_0%_100%/0.1)]`,
};

const Btn = ({ href, className, children }: { href: string; className: string; children: ReactNode }) =>
  href.startsWith("/login") || href.startsWith("http") || href.startsWith("#") ? (
    <a href={href} className={className}>{children}</a>
  ) : (
    <SmartLink href={href} className={className}>{children}</SmartLink>
  );

/** The top band: eyebrow, the page's only H1, one line, buttons, and who wrote it and when. */
export const Hero = ({
  eyebrow,
  title,
  lead,
  primary = { label: INBOX.cta, href: INBOX.startUrl },
  secondary,
  updated = INBOX.updated,
  children,
}: {
  eyebrow: string;
  title: ReactNode;
  lead: ReactNode;
  primary?: { label: string; href: string } | null;
  secondary?: { label: string; href: string };
  /** The page's own "Last updated" date, when it changed after the 2026-09-29 run. */
  updated?: string;
  children?: ReactNode;
}) => (
  <section className={`${tones.navy} relative overflow-hidden`}>
    <div aria-hidden className="cm-grid-dark pointer-events-none absolute inset-0 opacity-60" />
    <div className={`${wrap} relative py-16 sm:py-24`}>
      <p className="text-[14px] font-medium text-[hsl(216_85%_75%)]">{eyebrow}</p>
      <h1 className="mt-4 max-w-4xl text-[2.3rem] font-semibold leading-[1.06] tracking-[-0.035em] text-[hsl(0_0%_100%)] sm:text-5xl lg:text-[3.6rem]">
        {title}
      </h1>
      <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[hsl(0_0%_100%/0.78)] sm:text-xl">{lead}</p>
      {(primary || secondary) && (
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          {primary && <Btn href={primary.href} className={btn.white}>{primary.label}</Btn>}
          {secondary && <Btn href={secondary.href} className={btn.ghost}>{secondary.label}</Btn>}
        </div>
      )}
      <p className="mt-8 text-[14px] text-[hsl(0_0%_100%/0.6)]">
        {INBOX.team} · Last updated {updated}
      </p>
      {children}
    </div>
  </section>
);

/** A band with an H2 that opens with a short, direct answer. */
export const Band = ({
  tone = "pale",
  id,
  title,
  answer,
  children,
  narrow = false,
}: {
  tone?: Tone;
  id?: string;
  title: ReactNode;
  answer?: ReactNode;
  children?: ReactNode;
  narrow?: boolean;
}) => {
  const dark = isDark(tone);
  return (
    <section id={id} className={`${tones[tone]} py-16 sm:py-24`}>
      <div className={wrap}>
        <div className={narrow ? "max-w-3xl" : ""}>
          <h2 className={`max-w-3xl text-[1.9rem] font-semibold leading-[1.1] tracking-[-0.03em] sm:text-[2.5rem] ${dark ? "text-[hsl(0_0%_100%)]" : "text-[hsl(0_0%_4%)]"}`}>
            {title}
          </h2>
          {answer && (
            <p className={`mt-5 max-w-3xl text-[18px] leading-relaxed ${dark ? "text-[hsl(0_0%_100%/0.8)]" : "text-[hsl(215_16%_30%)]"}`}>{answer}</p>
          )}
          {children && <div className="mt-10">{children}</div>}
        </div>
      </div>
    </section>
  );
};

/** Body text inside a band. */
export const P = ({ children, dark = false }: { children: ReactNode; dark?: boolean }) => (
  <p className={`mt-4 max-w-3xl text-[16.5px] leading-relaxed ${dark ? "text-[hsl(0_0%_100%/0.75)]" : "text-[hsl(215_16%_30%)]"}`}>{children}</p>
);

export const H3 = ({ children, dark = false }: { children: ReactNode; dark?: boolean }) => (
  <h3 className={`mt-8 text-[1.25rem] font-semibold tracking-[-0.02em] ${dark ? "text-[hsl(0_0%_100%)]" : "text-[hsl(0_0%_4%)]"}`}>{children}</h3>
);

/** Plain list with small square markers (no ticks). */
export const List = ({ items, dark = false }: { items: ReactNode[]; dark?: boolean }) => (
  <ul className="mt-4 max-w-3xl space-y-2.5">
    {items.map((it, i) => (
      <li key={i} className={`flex gap-3 text-[16.5px] leading-relaxed ${dark ? "text-[hsl(0_0%_100%/0.8)]" : "text-[hsl(215_16%_30%)]"}`}>
        <span aria-hidden className={`mt-[0.62em] h-1.5 w-1.5 shrink-0 ${dark ? "bg-[hsl(0_0%_100%)]" : "bg-[hsl(0_0%_4%)]"}`} />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

/** The 40–60 word "What is" paragraph, on its own so it reads well when quoted. */
export const WhatIs = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className={`${tones.pale2} py-14 sm:py-20`}>
    <div className={wrap}>
      <div className="max-w-3xl rounded-2xl border border-[hsl(216_60%_85%)] bg-[hsl(0_0%_100%)] p-6 sm:p-9">
        <h2 className="text-[1.6rem] font-semibold tracking-[-0.025em] text-[hsl(0_0%_4%)] sm:text-[2rem]">{title}</h2>
        <p className="mt-4 text-[18px] leading-relaxed text-[hsl(215_16%_28%)]">{children}</p>
      </div>
    </div>
  </section>
);

/** Numbered steps. */
export const Steps = ({ items, dark = false }: { items: { title: string; body: ReactNode }[]; dark?: boolean }) => (
  <ol className="grid max-w-3xl gap-6">
    {items.map((s, i) => (
      <li key={s.title} className="flex gap-4">
        <span
          aria-hidden
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-[16px] font-semibold ${
            dark ? "bg-[hsl(0_0%_100%)] text-[hsl(0_0%_4%)]" : "bg-[hsl(0_0%_4%)] text-[hsl(0_0%_100%)]"
          }`}
        >
          {i + 1}
        </span>
        <div className="min-w-0 pt-1.5">
          <h3 className={`text-[1.15rem] font-semibold tracking-[-0.015em] ${dark ? "text-[hsl(0_0%_100%)]" : "text-[hsl(0_0%_4%)]"}`}>{s.title}</h3>
          <div className={`mt-1.5 text-[16px] leading-relaxed ${dark ? "text-[hsl(0_0%_100%/0.78)]" : "text-[hsl(215_16%_30%)]"}`}>{s.body}</div>
        </div>
      </li>
    ))}
  </ol>
);

/** A comparison or spec table. Scrolls sideways inside its own box on a phone, never the page. */
export const Table = ({ caption, head, rows, note }: { caption: string; head: string[]; rows: ReactNode[][]; note?: ReactNode }) => (
  <figure className="max-w-full">
    <div className="overflow-x-auto rounded-xl border border-[hsl(216_40%_84%)] bg-[hsl(0_0%_100%)]">
      <table className="w-full min-w-[640px] border-collapse text-left text-[15px]">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="bg-[hsl(216_60%_96%)]">
            {head.map((h, i) => (
              <th key={i} scope="col" className="border-b border-[hsl(216_40%_84%)] px-4 py-3 align-bottom font-semibold text-[hsl(0_0%_4%)]">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="align-top">
              {r.map((c, j) =>
                j === 0 ? (
                  <th key={j} scope="row" className="border-b border-[hsl(216_40%_90%)] px-4 py-3 font-medium text-[hsl(0_0%_4%)]">
                    {c}
                  </th>
                ) : (
                  <td key={j} className="border-b border-[hsl(216_40%_90%)] px-4 py-3 leading-relaxed text-[hsl(215_16%_30%)]">
                    {c}
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {note && <figcaption className="mt-3 text-[14px] leading-relaxed text-[hsl(215_14%_38%)]">{note}</figcaption>}
  </figure>
);

/** An outside link that names its source. */
export const Src = ({ href, children }: { href: string; children: ReactNode }) => (
  <a href={href} target="_blank" rel="noopener noreferrer nofollow" className="underline decoration-[hsl(216_30%_70%)] underline-offset-4 hover:decoration-[hsl(0_0%_4%)]">
    {children}
  </a>
);

/** An internal link with a descriptive anchor. */
export const In = ({ href, children }: { href: string; children: ReactNode }) => (
  <SmartLink href={href} className="font-medium text-[hsl(0_0%_4%)] underline decoration-[hsl(216_84%_52%/0.5)] underline-offset-4 hover:decoration-[hsl(216_84%_52%)]">
    {children}
  </SmartLink>
);

/**
 * A drawn workflow card: the sentence you type, and the steps Inbox makes from it.
 * The words match the step names the Inbox app uses; the names in it are examples.
 */
export const WorkflowCard = ({ sentence, steps, label = "Example workflow" }: { sentence: string; steps: string[]; label?: string }) => (
  <figure className="max-w-xl rounded-2xl border border-[hsl(216_40%_84%)] bg-[hsl(0_0%_100%)] p-5 sm:p-6">
    <figcaption className="text-[13px] font-medium uppercase tracking-[0.08em] text-[hsl(215_14%_40%)]">{label}</figcaption>
    <p className="mt-3 rounded-lg bg-[hsl(216_60%_96%)] px-4 py-3 text-[16px] leading-relaxed text-[hsl(0_0%_4%)]">“{sentence}”</p>
    <ol className="mt-4 space-y-2">
      {steps.map((s, i) => (
        <li key={i} className="flex items-start gap-3 text-[15px] text-[hsl(215_16%_28%)]">
          <span aria-hidden className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-[hsl(216_40%_80%)] text-[12px] font-semibold text-[hsl(0_0%_4%)]">
            {i + 1}
          </span>
          <span>{s}</span>
        </li>
      ))}
    </ol>
  </figure>
);

/** Questions with every answer in the HTML (details/summary), so search and AI tools can read them. */
export const Faqs = ({ items, tone = "white" }: { items: { q: string; a: ReactNode }[]; tone?: Tone }) => (
  <section className={`${tones[tone]} py-16 sm:py-24`}>
    <div className={`${wrap} grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16`}>
      <h2 className="text-[1.9rem] font-semibold leading-[1.1] tracking-[-0.03em] text-[hsl(0_0%_4%)] sm:text-[2.5rem]">Questions people ask</h2>
      <div className="divide-y divide-[hsl(216_30%_86%)] border-y border-[hsl(216_30%_86%)]">
        {items.map((it, i) => (
          <details key={it.q} open={i === 0} className="group py-5">
            <summary className="flex cursor-pointer list-none items-start justify-between gap-6 [&::-webkit-details-marker]:hidden">
              <h3 className="text-[17px] font-medium text-[hsl(0_0%_4%)]">{it.q}</h3>
              <span aria-hidden className="mt-0.5 text-[22px] leading-none text-[hsl(0_0%_4%)] transition-transform group-open:rotate-45">+</span>
            </summary>
            <div className="mt-3 max-w-3xl text-[16px] leading-relaxed text-[hsl(215_16%_30%)]">{it.a}</div>
          </details>
        ))}
      </div>
    </div>
  </section>
);

/** Links to related pages, each with a descriptive name. */
export const Related = ({ title = "Keep reading", links, tone = "pale" }: { title?: string; links: { href: string; label: string; desc: string }[]; tone?: Tone }) => (
  <section className={`${tones[tone]} py-16 sm:py-20`}>
    <div className={wrap}>
      <h2 className="text-[1.6rem] font-semibold tracking-[-0.025em] text-[hsl(0_0%_4%)] sm:text-[2rem]">{title}</h2>
      <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {links.map((l) => (
          <li key={l.href}>
            <SmartLink
              href={l.href}
              className="group flex h-full flex-col rounded-xl border border-[hsl(216_40%_84%)] bg-[hsl(0_0%_100%)] p-5 transition-colors hover:border-[hsl(216_84%_52%/0.5)]"
            >
              <span className="flex items-center justify-between gap-3 text-[16.5px] font-semibold text-[hsl(0_0%_4%)]">
                {l.label}
                <ArrowRight aria-hidden className="h-4 w-4 shrink-0 text-[hsl(0_0%_4%)] transition-transform group-hover:translate-x-0.5" />
              </span>
              <span className="mt-2 text-[15px] leading-relaxed text-[hsl(215_16%_32%)]">{l.desc}</span>
            </SmartLink>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

/** The closing call to action: one line and the same button as the top. */
export const Closing = ({ title, line, primary = { label: INBOX.cta, href: INBOX.startUrl }, secondary }: {
  title: string;
  line: string;
  primary?: { label: string; href: string };
  secondary?: { label: string; href: string };
}) => (
  <section className={`${tones.blue} relative overflow-hidden py-16 text-center sm:py-24`}>
    <div aria-hidden className="cm-grid-dark pointer-events-none absolute inset-0 opacity-50" />
    <div className={`${wrap} relative`}>
      <h2 className="mx-auto max-w-3xl text-[2rem] font-semibold leading-[1.08] tracking-[-0.03em] text-[hsl(0_0%_100%)] sm:text-[2.8rem]">{title}</h2>
      <p className="mx-auto mt-4 max-w-xl text-lg text-[hsl(0_0%_100%/0.85)]">{line}</p>
      <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <Btn href={primary.href} className={`${btn.white} w-full sm:w-auto`}>{primary.label}</Btn>
        {secondary && <Btn href={secondary.href} className={`${btn.ghost} w-full sm:w-auto`}>{secondary.label}</Btn>}
      </div>
      <p className="mt-5 text-[14px] text-[hsl(0_0%_100%/0.8)]">Free plan: 3 apps, 2 new workflows and 100 runs a month.</p>
    </div>
  </section>
);

/** Card grid for hubs. */
export const CardGrid = ({ items }: { items: { href: string; title: string; body: string; tag?: string }[] }) => (
  <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
    {items.map((c) => (
      <li key={c.href}>
        <SmartLink
          href={c.href}
          className="group flex h-full flex-col rounded-2xl border border-[hsl(216_40%_84%)] bg-[hsl(0_0%_100%)] p-6 transition-colors hover:border-[hsl(216_84%_52%/0.5)]"
        >
          {c.tag && <span className="text-[13px] font-medium text-[hsl(216_84%_42%)]">{c.tag}</span>}
          <h3 className="mt-1 text-[1.2rem] font-semibold tracking-[-0.02em] text-[hsl(0_0%_4%)]">{c.title}</h3>
          <p className="mt-2 flex-1 text-[15.5px] leading-relaxed text-[hsl(215_16%_32%)]">{c.body}</p>
          <span className="mt-5 inline-flex items-center gap-1.5 text-[15px] font-medium text-[hsl(0_0%_4%)]">
            Read more <ArrowRight aria-hidden className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </span>
        </SmartLink>
      </li>
    ))}
  </ul>
);

/**
 * The owner's copy method (gtm/playbooks/copy.md, 2026-10-15): one top question saying where it
 * happens (its last words blue and underlined), the reader's problems as centred sub-questions
 * with no card or bullet, the product clip, then one clean white card answering each question in
 * the same order, then one button. Added 2026-10-22 for the Gmail to Slack page and the free tool.
 */
export const ProblemFirst = ({
  question,
  highlight,
  problems,
  clip,
  promise,
  line,
  answers,
  cta = { label: INBOX.cta, href: INBOX.startUrl },
  tone = "pale",
}: {
  question: string;
  highlight: string;
  problems: string[];
  clip?: { src: string; alt: string; width: number; height: number };
  promise: string;
  line: ReactNode;
  answers: { title: string; body: ReactNode }[];
  cta?: { label: string; href: string } | null;
  tone?: Tone;
}) => (
  <section className={`${tones[tone]} py-14 sm:py-20`}>
    <div className={`${wrap} text-center`}>
      <h2 className="mx-auto max-w-3xl text-[24px] font-semibold leading-[1.2] tracking-[-0.025em] text-[hsl(0_0%_4%)] sm:text-[30px]">
        {question}{" "}
        <span className="whitespace-nowrap border-b-[3px] border-[hsl(216_84%_52%)] text-[hsl(216_84%_46%)]">{highlight}</span>
      </h2>
      <ul className="mx-auto mt-6 max-w-2xl space-y-2">
        {problems.map((p) => (
          <li key={p} className="text-[17px] font-semibold leading-snug text-[hsl(0_0%_10%)] sm:text-[19px]">{p}</li>
        ))}
      </ul>
      {clip && (
        <div className="mx-auto mt-8 max-w-2xl overflow-hidden rounded-2xl border border-[hsl(216_30%_86%)] bg-[hsl(0_0%_100%)] shadow-[0_12px_40px_-18px_hsl(222_55%_9%/0.35)]">
          <img src={clip.src} alt={clip.alt} width={clip.width} height={clip.height} loading="lazy" decoding="async" className="block h-auto w-full" />
        </div>
      )}
      <div className="mx-auto mt-6 max-w-2xl rounded-2xl border border-[hsl(216_20%_86%)] bg-[hsl(0_0%_100%)] p-6 text-left shadow-[0_10px_30px_-20px_hsl(222_55%_9%/0.3)] sm:p-8">
        <p className="text-[19px] font-semibold tracking-[-0.015em] text-[hsl(0_0%_4%)] sm:text-[21px]">{promise}</p>
        <p className="mt-2 text-[16px] leading-relaxed text-[hsl(215_16%_30%)]">{line}</p>
        <div className="mt-5 grid gap-x-8 gap-y-4 border-t border-[hsl(216_20%_88%)] pt-5 sm:grid-cols-2">
          {answers.map((a) => (
            <div key={a.title} className="min-w-0">
              <p className="text-[16px] font-semibold text-[hsl(0_0%_4%)]">{a.title}</p>
              <p className="mt-1 text-[15px] leading-relaxed text-[hsl(215_14%_38%)]">{a.body}</p>
            </div>
          ))}
        </div>
      </div>
      {cta && (
        <div className="mt-7">
          <Btn href={cta.href} className={btn.ink}>{cta.label}</Btn>
        </div>
      )}
    </div>
  </section>
);
